import React, { useState } from 'react';
import {
  Card, Steps, Form, Input, InputNumber, Button, Upload, message,
  Progress, Space, Divider, Select, DatePicker, Switch, Row, Col,
  Typography, Collapse, Tag, Tooltip, Alert, Table, Tabs
} from 'antd';
import {
  UploadOutlined, SaveOutlined, QrcodeOutlined, PlusOutlined,
  DeleteOutlined, InfoCircleOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import type {
  DppFormData, DppFormProduct, DppFormOrganisation, DppFormBrand,
  DppFormCategory, DppFormCertification, DppFormAttribute,
  DppFormObservation, DppFormOffer, DppFormMediaObject,
  ProductGroup, ProductModel, Property, Unit, Value,
  PropertyValueUnit as PropertyValueUnitType,
  Linking, LinkingType, Person, Currency, Language, Dictionary, Thing
} from '../../types/dpp';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const emptyProduct: DppFormProduct = {};
const emptyOrg: DppFormOrganisation = {};
const emptyBrand: DppFormBrand = {};

const DppCreation: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [product, setProduct] = useState<DppFormProduct>(emptyProduct);
  const [manufacturer, setManufacturer] = useState<DppFormOrganisation>(emptyOrg);
  const [brand, setBrand] = useState<DppFormBrand>(emptyBrand);
  const [categories, setCategories] = useState<DppFormCategory[]>([{}]);
  const [certifications, setCertifications] = useState<DppFormCertification[]>([]);
  const [attributes, setAttributes] = useState<DppFormAttribute[]>([]);
  const [observations, setObservations] = useState<DppFormObservation[]>([]);
  const [mediaObjects, setMediaObjects] = useState<DppFormMediaObject[]>([]);

  // New schema states
  const [productGroup, setProductGroup] = useState<Partial<ProductGroup>>({});
  const [productModel, setProductModel] = useState<Partial<ProductModel>>({});
  const [person, setPerson] = useState<Partial<Person>>({});
  const [properties, setProperties] = useState<Partial<Property>[]>([]);
  const [units, setUnits] = useState<Partial<Unit>[]>([]);
  const [values, setValues] = useState<Partial<Value>[]>([]);
  const [pvUnits, setPvUnits] = useState<Partial<PropertyValueUnitType>[]>([]);
  const [linkings, setLinkings] = useState<Partial<Linking>[]>([]);
  const [linkingTypes, setLinkingTypes] = useState<Partial<LinkingType>[]>([]);
  const [currencies, setCurrencies] = useState<Partial<Currency>[]>([]);
  const [languages, setLanguages] = useState<Partial<Language>[]>([]);
  const [dictionaries, setDictionaries] = useState<Partial<Dictionary>[]>([]);
  const [things, setThings] = useState<Partial<Thing>[]>([]);
  const [offers, setOffers] = useState<DppFormOffer[]>([]);

  const steps = [
    { title: 'Produkt' },
    { title: 'Hersteller & Marke' },
    { title: 'Klassifikation' },
    { title: 'Eigenschaften' },
    { title: 'Zertifizierungen' },
    { title: 'Medien & Abschluss' },
  ];

  const nextStep = () => {
    form.validateFields().then(() => {
      setCurrentStep(currentStep + 1);
    }).catch(() => {
      message.warning('Bitte füllen Sie die Pflichtfelder aus.');
    });
  };

  const prevStep = () => setCurrentStep(currentStep - 1);

  const addCertification = () => setCertifications([...certifications, {}]);
  const removeCertification = (i: number) => setCertifications(certifications.filter((_, idx) => idx !== i));

  const addAttribute = () => setAttributes([...attributes, {}]);
  const removeAttribute = (i: number) => setAttributes(attributes.filter((_, idx) => idx !== i));

  const addObservation = () => setObservations([...observations, {}]);
  const removeObservation = (i: number) => setObservations(observations.filter((_, idx) => idx !== i));

  const addMediaObject = () => setMediaObjects([...mediaObjects, {}]);
  const removeMediaObject = (i: number) => setMediaObjects(mediaObjects.filter((_, idx) => idx !== i));

  const onSubmit = async () => {
    setLoading(true);
    setProgress(0);
    try {
      for (let i = 0; i <= 100; i += 10) {
        setProgress(i);
        await new Promise(resolve => setTimeout(resolve, 150));
      }
      const dppData: DppFormData = {
        product, manufacturer, brand, categories,
        certifications, attributes, observations,
        offers: [], mediaObjects,
      };
      const qrPayload = JSON.stringify({
        dpp_id: `DPP-${Date.now()}`,
        product: { name: product.name, gtin: product.gtin, mpn: product.mpn },
        manufacturer: { legalName: manufacturer.legalName },
        created_at: new Date().toISOString(),
      });
      const qrCodeDataUrl = await QRCode.toDataURL(qrPayload);
      setQrCodeUrl(qrCodeDataUrl);
      message.success('DPP erfolgreich erstellt!');
      console.log('DPP Data:', dppData);
    } catch (error) {
      message.error('Fehler bei der DPP-Erstellung');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 0: Product Identification ──
  const renderProductStep = () => (
    <Tabs defaultActiveKey="product" items={[
      {
        key: 'product',
        label: 'Product',
        children: (
          <div>
            <Alert message="Felder gem. ProductRead-Schema. Pflichtfelder sind mit * markiert." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="name *" required>
                  <Input value={product.name} onChange={e => setProduct({ ...product, name: e.target.value })} placeholder="Automotive Connector Module" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="legalName">
                  <Input value={product.legalName} onChange={e => setProduct({ ...product, legalName: e.target.value })} placeholder="Offizieller Produktname" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="description *" required>
                  <TextArea rows={3} value={product.description} onChange={e => setProduct({ ...product, description: e.target.value })} placeholder="Produktbeschreibung" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="disambiguatingDescription">
                  <Input value={product.disambiguatingDescription} onChange={e => setProduct({ ...product, disambiguatingDescription: e.target.value })} placeholder="Kurzbezeichnung zur Unterscheidung" />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">Artikelnummern</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label={<Tooltip title="Global Trade Item Number (integer)"><span>gtin <InfoCircleOutlined /></span></Tooltip>}>
                  <Input value={product.gtin} onChange={e => setProduct({ ...product, gtin: e.target.value })} placeholder="4012345000001" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="gtin8">
                  <Input value={product.gtin8} onChange={e => setProduct({ ...product, gtin8: e.target.value })} placeholder="40123450" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="gtin13">
                  <Input value={product.gtin13} onChange={e => setProduct({ ...product, gtin13: e.target.value })} placeholder="4012345000001" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="gtin14">
                  <Input value={product.gtin14} onChange={e => setProduct({ ...product, gtin14: e.target.value })} placeholder="04012345000001" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label={<Tooltip title="Manufacturer Part Number (string, max 128)"><span>mpn <InfoCircleOutlined /></span></Tooltip>}>
                  <Input value={product.mpn} onChange={e => setProduct({ ...product, mpn: e.target.value })} placeholder="BMW-12345" maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={<Tooltip title="Stock Keeping Unit (string, max 32)"><span>sku <InfoCircleOutlined /></span></Tooltip>}>
                  <Input value={product.sku} onChange={e => setProduct({ ...product, sku: e.target.value })} placeholder="SKU-001" maxLength={32} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={<Tooltip title="NATO Stock Number (string, max 32)"><span>nsn <InfoCircleOutlined /></span></Tooltip>}>
                  <Input value={product.nsn} onChange={e => setProduct({ ...product, nsn: e.target.value })} placeholder="5962-01-234-5678" maxLength={32} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="asin">
                  <Input value={product.asin} onChange={e => setProduct({ ...product, asin: e.target.value })} placeholder="B08N5WRWNW" />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">Identifikatoren</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="cdmmId">
                  <Input value={product.cdmmId} onChange={e => setProduct({ ...product, cdmmId: e.target.value })} placeholder="CDMM-Identifier" maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="assetId">
                  <Input value={product.assetId} onChange={e => setProduct({ ...product, assetId: e.target.value })} placeholder="Asset-Identifier" maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="globalAssetId">
                  <Input value={product.globalAssetId} onChange={e => setProduct({ ...product, globalAssetId: e.target.value })} placeholder="Global Asset-Identifier" maxLength={256} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="uri">
                  <Input value={product.uri} onChange={e => setProduct({ ...product, uri: e.target.value })} placeholder="https://..." />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="irdi">
                  <Input value={product.irdi} onChange={e => setProduct({ ...product, irdi: e.target.value })} placeholder="IRDI-Code" maxLength={64} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="url">
                  <Input value={product.url} onChange={e => setProduct({ ...product, url: e.target.value })} placeholder="https://product-page.com" />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">Modell & Herkunft</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="model">
                  <Input value={product.model} onChange={e => setProduct({ ...product, model: e.target.value })} placeholder="Modellbezeichnung" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="countryOfAssembly">
                  <Input value={product.countryOfAssembly} onChange={e => setProduct({ ...product, countryOfAssembly: e.target.value })} placeholder="DE" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="countryOfLastProcessing">
                  <Input value={product.countryOfLastProcessing} onChange={e => setProduct({ ...product, countryOfLastProcessing: e.target.value })} placeholder="DE" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="releaseDate">
                  <Input type="date" value={product.releaseDate} onChange={e => setProduct({ ...product, releaseDate: e.target.value })} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="validFrom">
                  <Input type="date" value={product.validFrom} onChange={e => setProduct({ ...product, validFrom: e.target.value })} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="validThrough">
                  <Input type="date" value={product.validThrough} onChange={e => setProduct({ ...product, validThrough: e.target.value })} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="notesProductManager">
              <TextArea rows={2} value={product.notesProductManager} onChange={e => setProduct({ ...product, notesProductManager: e.target.value })} placeholder="Interne Notizen" />
            </Form.Item>
          </div>
        ),
      },
      {
        key: 'productGroup',
        label: 'ProductGroup',
        children: (
          <div>
            <Alert message="ProductGroupRead – Gruppierung von Produkten mit zugeordneten Properties." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="name">
                  <Input value={productGroup.name} onChange={e => setProductGroup({ ...productGroup, name: e.target.value })} placeholder="Steckverbinder HV" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="productGroupId">
                  <InputNumber style={{ width: '100%' }} value={productGroup.productGroupId} onChange={v => setProductGroup({ ...productGroup, productGroupId: v ?? undefined })} placeholder="ID" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="url">
                  <Input value={productGroup.url} onChange={e => setProductGroup({ ...productGroup, url: e.target.value })} placeholder="https://..." />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="cdmmId">
                  <Input value={productGroup.cdmmId} onChange={e => setProductGroup({ ...productGroup, cdmmId: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="assetId">
                  <Input value={productGroup.assetId} onChange={e => setProductGroup({ ...productGroup, assetId: e.target.value })} maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="globalAssetId">
                  <Input value={productGroup.globalAssetId} onChange={e => setProductGroup({ ...productGroup, globalAssetId: e.target.value })} maxLength={256} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="description">
              <TextArea rows={2} value={productGroup.description} onChange={e => setProductGroup({ ...productGroup, description: e.target.value })} placeholder="Beschreibung der Produktgruppe" />
            </Form.Item>
          </div>
        ),
      },
      {
        key: 'productModel',
        label: 'ProductModel',
        children: (
          <div>
            <Alert message="ProductModelRead – Modellvariante eines Produkts mit Kategorie- und Property-Zuordnung." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="productModelId">
                  <InputNumber style={{ width: '100%' }} value={productModel.productModelId} onChange={v => setProductModel({ ...productModel, productModelId: v ?? undefined })} placeholder="ID" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="name">
                  <Input value={productModel.name} onChange={e => setProductModel({ ...productModel, name: e.target.value })} placeholder="HV480-R1" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="url">
                  <Input value={productModel.url} onChange={e => setProductModel({ ...productModel, url: e.target.value })} placeholder="https://..." />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="cdmmId">
                  <Input value={productModel.cdmmId} onChange={e => setProductModel({ ...productModel, cdmmId: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="assetId">
                  <Input value={productModel.assetId} onChange={e => setProductModel({ ...productModel, assetId: e.target.value })} maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="globalAssetId">
                  <Input value={productModel.globalAssetId} onChange={e => setProductModel({ ...productModel, globalAssetId: e.target.value })} maxLength={256} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="description">
              <TextArea rows={2} value={productModel.description} onChange={e => setProductModel({ ...productModel, description: e.target.value })} placeholder="Beschreibung des Produktmodells" />
            </Form.Item>
          </div>
        ),
      },
    ]} />
  );

  // ── Step 1: Manufacturer & Brand ──
  const renderManufacturerStep = () => (
    <Tabs defaultActiveKey="organisation" items={[
      {
        key: 'organisation',
        label: 'Organisation',
        children: (
          <div>
            <Alert message="OrganisationRead – Hersteller mit PostalAddress und ContactPoint." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="legalName *" required>
                  <Input value={manufacturer.legalName} onChange={e => setManufacturer({ ...manufacturer, legalName: e.target.value })} placeholder="ABC Zulieferer GmbH" maxLength={512} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="additionalName">
                  <Input value={manufacturer.additionalName} onChange={e => setManufacturer({ ...manufacturer, additionalName: e.target.value })} placeholder="Zweigniederlassung" maxLength={256} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="taxId">
                  <Input value={manufacturer.taxId} onChange={e => setManufacturer({ ...manufacturer, taxId: e.target.value })} placeholder="DE123456789" maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="globalLocationNumber">
                  <Input value={manufacturer.globalLocationNumber} onChange={e => setManufacturer({ ...manufacturer, globalLocationNumber: e.target.value })} placeholder="4012345000001" maxLength={512} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="location">
                  <Input value={manufacturer.location} onChange={e => setManufacturer({ ...manufacturer, location: e.target.value })} placeholder="Stuttgart, Deutschland" maxLength={512} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="iso6523Code">
                  <Input value={manufacturer.iso6523Code} onChange={e => setManufacturer({ ...manufacturer, iso6523Code: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="isicV4">
                  <Input value={manufacturer.isicV4} onChange={e => setManufacturer({ ...manufacturer, isicV4: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="leiCode">
                  <Input value={manufacturer.leiCode} onChange={e => setManufacturer({ ...manufacturer, leiCode: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">PostalAddress</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="streetAddress">
                  <Input value={manufacturer.streetAddress} onChange={e => setManufacturer({ ...manufacturer, streetAddress: e.target.value })} placeholder="Musterstraße 1" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="postalCode">
                  <Input value={manufacturer.postalCode} onChange={e => setManufacturer({ ...manufacturer, postalCode: e.target.value })} placeholder="70173" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="addressLocality">
                  <Input value={manufacturer.addressLocality} onChange={e => setManufacturer({ ...manufacturer, addressLocality: e.target.value })} placeholder="Stuttgart" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="addressRegion">
                  <Input value={manufacturer.addressRegion} onChange={e => setManufacturer({ ...manufacturer, addressRegion: e.target.value })} placeholder="Baden-Württemberg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="addressCountry">
                  <Input value={manufacturer.addressCountry} onChange={e => setManufacturer({ ...manufacturer, addressCountry: e.target.value })} placeholder="DE" />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">ContactPoint</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="telephone">
                  <Input value={manufacturer.telephone} onChange={e => setManufacturer({ ...manufacturer, telephone: e.target.value })} placeholder="+49 711 1234567" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="email">
                  <Input value={manufacturer.email} onChange={e => setManufacturer({ ...manufacturer, email: e.target.value })} placeholder="info@company.com" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="faxNumber">
                  <Input value={manufacturer.faxNumber} onChange={e => setManufacturer({ ...manufacturer, faxNumber: e.target.value })} placeholder="+49 711 1234568" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="contactType">
                  <Select value={manufacturer.contactType} onChange={v => setManufacturer({ ...manufacturer, contactType: v })} placeholder="Auswählen">
                    <Option value="sales">Vertrieb</Option>
                    <Option value="support">Support</Option>
                    <Option value="technical">Technik</Option>
                    <Option value="general">Allgemein</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="url">
              <Input value={manufacturer.url} onChange={e => setManufacturer({ ...manufacturer, url: e.target.value })} placeholder="https://www.company.com" />
            </Form.Item>
          </div>
        ),
      },
      {
        key: 'person',
        label: 'Person',
        children: (
          <div>
            <Alert message="PersonRead – Ansprechpartner / Kontaktperson beim Hersteller." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="givenName">
                  <Input value={person.givenName} onChange={e => setPerson({ ...person, givenName: e.target.value })} placeholder="Max" maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="additionalName">
                  <Input value={person.additionalName} onChange={e => setPerson({ ...person, additionalName: e.target.value })} placeholder="Dr." maxLength={256} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="familyName">
                  <Input value={person.familyName} onChange={e => setPerson({ ...person, familyName: e.target.value })} placeholder="Mustermann" maxLength={256} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="legalName">
                  <Input value={person.legalName} onChange={e => setPerson({ ...person, legalName: e.target.value })} placeholder="Dr. Max Mustermann" maxLength={512} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="taxId">
                  <Input value={person.taxId} onChange={e => setPerson({ ...person, taxId: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="location">
                  <Input value={person.location} onChange={e => setPerson({ ...person, location: e.target.value })} placeholder="Stuttgart" maxLength={512} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="iso6523Code">
                  <Input value={person.iso6523Code} onChange={e => setPerson({ ...person, iso6523Code: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="isicV4">
                  <Input value={person.isicV4} onChange={e => setPerson({ ...person, isicV4: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="leiCode">
                  <Input value={person.leiCode} onChange={e => setPerson({ ...person, leiCode: e.target.value })} maxLength={128} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="globalLocationNumber">
                  <Input value={person.globalLocationNumber} onChange={e => setPerson({ ...person, globalLocationNumber: e.target.value })} maxLength={512} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="url">
                  <Input value={person.url} onChange={e => setPerson({ ...person, url: e.target.value })} placeholder="https://..." />
                </Form.Item>
              </Col>
            </Row>
          </div>
        ),
      },
      {
        key: 'brand',
        label: 'Brand',
        children: (
          <div>
            <Alert message="BrandRead – Marke des Produkts." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="name *" required>
                  <Input value={brand.name} onChange={e => setBrand({ ...brand, name: e.target.value })} placeholder="AutoParts Pro" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="slogan">
                  <Input value={brand.slogan} onChange={e => setBrand({ ...brand, slogan: e.target.value })} placeholder="Quality First" maxLength={512} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="url">
                  <Input value={brand.url} onChange={e => setBrand({ ...brand, url: e.target.value })} placeholder="https://brand.com" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        ),
      },
    ]} />
  );

  // ── Step 2: Classification ──
  const renderClassificationStep = () => (
    <Tabs defaultActiveKey="classification" items={[
      {
        key: 'classification',
        label: 'Klassifikation',
        children: (
          <div>
            <Alert message="Product.eclass, Product.hsCode6/8/11 – Klassifikationscodes am Produkt." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={<Tooltip title="Product.eclass (string, max 2048)"><span>eclass <InfoCircleOutlined /></span></Tooltip>}>
                  <Input value={product.eclass} onChange={e => setProduct({ ...product, eclass: e.target.value })} placeholder="27-27-04-08" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="hsCode6">
                  <Input value={product.hsCode6} onChange={e => setProduct({ ...product, hsCode6: e.target.value })} placeholder="854449" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="hsCode8">
                  <Input value={product.hsCode8} onChange={e => setProduct({ ...product, hsCode8: e.target.value })} placeholder="85444990" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="hsCode11">
                  <Input value={product.hsCode11} onChange={e => setProduct({ ...product, hsCode11: e.target.value })} placeholder="85444990000" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        ),
      },
      {
        key: 'category',
        label: 'Category',
        children: (
          <div>
            <Alert message="CategoryRead – Produktkategorien mit Hierarchie und Codes." type="info" showIcon style={{ marginBottom: 16 }} />
            {categories.map((cat, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Category ${i + 1}`}
                extra={categories.length > 1 ? <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setCategories(categories.filter((_, idx) => idx !== i))} /> : null}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label="category">
                      <Input value={cat.category} onChange={e => { const c = [...categories]; c[i] = { ...c[i], category: e.target.value }; setCategories(c); }} placeholder="Automotive" maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="codeValue">
                      <Input value={cat.codeValue} onChange={e => { const c = [...categories]; c[i] = { ...c[i], codeValue: e.target.value }; setCategories(c); }} placeholder="27-27" maxLength={32} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="level">
                      <Input value={cat.level} onChange={e => { const c = [...categories]; c[i] = { ...c[i], level: e.target.value }; setCategories(c); }} placeholder="3" maxLength={9} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="categoryHierarchy">
                      <Input value={cat.categoryHierarchy} onChange={e => { const c = [...categories]; c[i] = { ...c[i], categoryHierarchy: e.target.value }; setCategories(c); }} placeholder="Automotive > Electrical" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setCategories([...categories, {}])} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
              Category hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'language',
        label: 'Language / Dictionary',
        children: (
          <div>
            <Alert message="LanguageRead & DictionaryRead – Sprachen und mehrsprachige Wörterbucheinträge." type="info" showIcon style={{ marginBottom: 16 }} />
            <Title level={5}>Language</Title>
            {languages.map((lang, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Language ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setLanguages(languages.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="languageName">
                      <Input value={lang.languageName} onChange={e => { const l = [...languages]; l[i] = { ...l[i], languageName: e.target.value }; setLanguages(l); }} placeholder="Deutsch" maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="languageCodeAlpha2">
                      <Input value={lang.languageCodeAlpha2} onChange={e => { const l = [...languages]; l[i] = { ...l[i], languageCodeAlpha2: e.target.value }; setLanguages(l); }} placeholder="de" maxLength={5} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="languageCodeAlpha3">
                      <Input value={lang.languageCodeAlpha3} onChange={e => { const l = [...languages]; l[i] = { ...l[i], languageCodeAlpha3: e.target.value }; setLanguages(l); }} placeholder="deu" maxLength={5} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="lcid">
                      <Input value={lang.lcid} onChange={e => { const l = [...languages]; l[i] = { ...l[i], lcid: e.target.value }; setLanguages(l); }} placeholder="de-DE" maxLength={64} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setLanguages([...languages, {}])} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
              Language hinzufügen
            </Button>

            <Title level={5}>Dictionary</Title>
            {dictionaries.map((dict, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Dictionary ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setDictionaries(dictionaries.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="dataEntry">
                      <TextArea rows={2} value={dict.dataEntry} onChange={e => { const d = [...dictionaries]; d[i] = { ...d[i], dataEntry: e.target.value }; setDictionaries(d); }} placeholder="Übersetzungstext" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="name">
                      <Input value={dict.name} onChange={e => { const d = [...dictionaries]; d[i] = { ...d[i], name: e.target.value }; setDictionaries(d); }} placeholder="Bezeichnung" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setDictionaries([...dictionaries, {}])} icon={<PlusOutlined />} block>
              Dictionary hinzufügen
            </Button>
          </div>
        ),
      },
    ]} />
  );

  // ── Step 3: Attributes & Observations ──
  const renderAttributesStep = () => (
    <Tabs defaultActiveKey="ppv" items={[
      {
        key: 'ppv',
        label: 'ProductPropertyValue',
        children: (
          <div>
            <Alert message="ProductPropertyValueRead – Eigenschaftswerte mit Unit, Min/Max-Bereichen." type="info" showIcon style={{ marginBottom: 16 }} />
            {attributes.map((attr, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`ProductPropertyValue ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeAttribute(i)} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={attr.propertyName} onChange={e => { const a = [...attributes]; a[i] = { ...a[i], propertyName: e.target.value }; setAttributes(a); }} placeholder="Gewicht" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="propertyValueUnit">
                      <Input value={attr.propertyUnit} onChange={e => { const a = [...attributes]; a[i] = { ...a[i], propertyUnit: e.target.value }; setAttributes(a); }} placeholder="kg" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="valueText">
                      <Input value={attr.valueText} onChange={e => { const a = [...attributes]; a[i] = { ...a[i], valueText: e.target.value }; setAttributes(a); }} placeholder="Wert als Text" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label="valueWholeNumber">
                      <InputNumber style={{ width: '100%' }} value={attr.valueWholeNumber} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], valueWholeNumber: v ?? undefined }; setAttributes(a); }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="valueFloatingPointNumber">
                      <InputNumber style={{ width: '100%' }} step={0.01} value={attr.valueFloatingPointNumber} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], valueFloatingPointNumber: v ?? undefined }; setAttributes(a); }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="valueMinFloatingPointNumber">
                      <InputNumber style={{ width: '100%' }} step={0.01} value={attr.valueMinFloatingPointNumber} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], valueMinFloatingPointNumber: v ?? undefined }; setAttributes(a); }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="valueMaxFloatingPointNumber">
                      <InputNumber style={{ width: '100%' }} step={0.01} value={attr.valueMaxFloatingPointNumber} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], valueMaxFloatingPointNumber: v ?? undefined }; setAttributes(a); }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={6}><Form.Item label="keyFeature"><Switch checked={attr.keyFeature} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], keyFeature: v }; setAttributes(a); }} /></Form.Item></Col>
                  <Col span={6}><Form.Item label="keyFilter"><Switch checked={attr.keyFilter} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], keyFilter: v }; setAttributes(a); }} /></Form.Item></Col>
                  <Col span={6}><Form.Item label="trueFalse"><Switch checked={attr.trueFalse} onChange={v => { const a = [...attributes]; a[i] = { ...a[i], trueFalse: v }; setAttributes(a); }} /></Form.Item></Col>
                  <Col span={6}><Form.Item label="sortOrder"><Input value={attr.sortOrder} onChange={e => { const a = [...attributes]; a[i] = { ...a[i], sortOrder: e.target.value }; setAttributes(a); }} placeholder="10" /></Form.Item></Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={addAttribute} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
              ProductPropertyValue hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'property-unit-value',
        label: 'Property / Unit / Value',
        children: (
          <div>
            <Alert message="PropertyRead, UnitRead, ValueRead – Stammdaten für Eigenschaftsdefinitionen und Maßeinheiten." type="info" showIcon style={{ marginBottom: 16 }} />
            <Title level={5}>Property</Title>
            {properties.map((prop, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Property ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setProperties(properties.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={prop.name} onChange={e => { const p = [...properties]; p[i] = { ...p[i], name: e.target.value }; setProperties(p); }} placeholder="Gewicht" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="cdmmId">
                      <Input value={prop.cdmmId} onChange={e => { const p = [...properties]; p[i] = { ...p[i], cdmmId: e.target.value }; setProperties(p); }} maxLength={128} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="irdi">
                      <Input value={prop.irdi} onChange={e => { const p = [...properties]; p[i] = { ...p[i], irdi: e.target.value }; setProperties(p); }} maxLength={64} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={4}><Form.Item label="text"><Switch checked={prop.text} onChange={v => { const p = [...properties]; p[i] = { ...p[i], text: v }; setProperties(p); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="multilineText"><Switch checked={prop.multilineText} onChange={v => { const p = [...properties]; p[i] = { ...p[i], multilineText: v }; setProperties(p); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="wholeNumber"><Switch checked={prop.wholeNumber} onChange={v => { const p = [...properties]; p[i] = { ...p[i], wholeNumber: v }; setProperties(p); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="floatingPointNumber"><Switch checked={prop.floatingPointNumber} onChange={v => { const p = [...properties]; p[i] = { ...p[i], floatingPointNumber: v }; setProperties(p); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="singleSelect"><Switch checked={prop.singleSelect} onChange={v => { const p = [...properties]; p[i] = { ...p[i], singleSelect: v }; setProperties(p); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="multiSelect"><Switch checked={prop.multiSelect} onChange={v => { const p = [...properties]; p[i] = { ...p[i], multiSelect: v }; setProperties(p); }} /></Form.Item></Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setProperties([...properties, {}])} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
              Property hinzufügen
            </Button>

            <Title level={5}>Unit</Title>
            {units.map((u, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Unit ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setUnits(units.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={u.name} onChange={e => { const arr = [...units]; arr[i] = { ...arr[i], name: e.target.value }; setUnits(arr); }} placeholder="Kilogramm" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="DSI">
                      <Input value={u.DSI} onChange={e => { const arr = [...units]; arr[i] = { ...arr[i], DSI: e.target.value }; setUnits(arr); }} placeholder="kg" maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="irdi">
                      <Input value={u.irdi} onChange={e => { const arr = [...units]; arr[i] = { ...arr[i], irdi: e.target.value }; setUnits(arr); }} maxLength={64} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setUnits([...units, {}])} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
              Unit hinzufügen
            </Button>

            <Title level={5}>Value</Title>
            {values.map((v, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Value ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setValues(values.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="name">
                      <Input value={v.name} onChange={e => { const arr = [...values]; arr[i] = { ...arr[i], name: e.target.value }; setValues(arr); }} placeholder="Wertbezeichnung" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="cdmmId">
                      <Input value={v.cdmmId} onChange={e => { const arr = [...values]; arr[i] = { ...arr[i], cdmmId: e.target.value }; setValues(arr); }} maxLength={128} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setValues([...values, {}])} icon={<PlusOutlined />} block>
              Value hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'observation',
        label: 'Observation',
        children: (
          <div>
            <Alert message="ObservationRead – Messungen und Beobachtungen am Produkt." type="info" showIcon style={{ marginBottom: 16 }} />
            {observations.map((obs, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Observation ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeObservation(i)} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={obs.name} onChange={e => { const o = [...observations]; o[i] = { ...o[i], name: e.target.value }; setObservations(o); }} placeholder="CO2-Messung" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="variableMeasured">
                      <Input value={obs.variableMeasured} onChange={e => { const o = [...observations]; o[i] = { ...o[i], variableMeasured: e.target.value }; setObservations(o); }} placeholder="CO2-Emission" maxLength={128} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="observationDate">
                      <Input type="date" value={obs.observationDate} onChange={e => { const o = [...observations]; o[i] = { ...o[i], observationDate: e.target.value }; setObservations(o); }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="measurementMethod">
                      <Input value={obs.measurementMethod} onChange={e => { const o = [...observations]; o[i] = { ...o[i], measurementMethod: e.target.value }; setObservations(o); }} placeholder="ISO 14064" maxLength={512} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="measurementTechnique">
                      <Input value={obs.measurementTechnique} onChange={e => { const o = [...observations]; o[i] = { ...o[i], measurementTechnique: e.target.value }; setObservations(o); }} placeholder="Sensor XY" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="marginOfError">
                      <Input value={obs.marginOfError} onChange={e => { const o = [...observations]; o[i] = { ...o[i], marginOfError: e.target.value }; setObservations(o); }} placeholder="+-0.5%" maxLength={32} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={addObservation} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
              Observation hinzufügen
            </Button>
          </div>
        ),
      },
    ]} />
  );

  // ── Step 4: Certifications & Linking ──
  const renderCertificationsStep = () => (
    <Tabs defaultActiveKey="certification" items={[
      {
        key: 'certification',
        label: 'Certification',
        children: (
          <div>
            <Alert message="CertificationRead – Zertifizierungen und Prüfnachweise." type="info" showIcon style={{ marginBottom: 16 }} />
            {certifications.map((cert, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Certification ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeCertification(i)} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={cert.name} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], name: e.target.value }; setCertifications(c); }} placeholder="ISO 9001" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="certificationIdentification">
                      <Input value={cert.certificationIdentification} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], certificationIdentification: e.target.value }; setCertifications(c); }} placeholder="CERT-2024-001" maxLength={128} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="certificationStatus">
                      <Select value={cert.certificationStatus} onChange={v => { const c = [...certifications]; c[i] = { ...c[i], certificationStatus: v }; setCertifications(c); }} placeholder="Status">
                        <Option value="active">Aktiv</Option>
                        <Option value="expired">Abgelaufen</Option>
                        <Option value="pending">Ausstehend</Option>
                        <Option value="revoked">Widerrufen</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="about">
                      <Input value={cert.about} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], about: e.target.value }; setCertifications(c); }} placeholder="Qualitätsmanagement" maxLength={512} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="auditDate">
                      <Input type="date" value={cert.auditDate} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], auditDate: e.target.value }; setCertifications(c); }} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="hasMeasurement">
                      <Input value={cert.hasMeasurement} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], hasMeasurement: e.target.value }; setCertifications(c); }} placeholder="Messwert" maxLength={128} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="validFrom">
                      <Input type="date" value={cert.validFrom} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], validFrom: e.target.value }; setCertifications(c); }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="validThrough">
                      <Input type="date" value={cert.validThrough} onChange={e => { const c = [...certifications]; c[i] = { ...c[i], validThrough: e.target.value }; setCertifications(c); }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={addCertification} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
              Certification hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'linking',
        label: 'Linking / LinkingType',
        children: (
          <div>
            <Alert message="LinkingRead & LinkingTypeRead – Verknüpfungen zwischen Entitäten (Produkt, Kategorie, MediaObject, etc.)." type="info" showIcon style={{ marginBottom: 16 }} />
            <Title level={5}>Linking</Title>
            {linkings.map((lnk, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Linking ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setLinkings(linkings.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="templateName">
                      <Input value={lnk.templateName} onChange={e => { const l = [...linkings]; l[i] = { ...l[i], templateName: e.target.value }; setLinkings(l); }} placeholder="product-category" maxLength={256} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="categoryHierarchy">
                      <Input value={lnk.categoryHierarchy} onChange={e => { const l = [...linkings]; l[i] = { ...l[i], categoryHierarchy: e.target.value }; setLinkings(l); }} placeholder="Automotive > Electrical" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="sortOrder">
                      <Input value={lnk.sortOrder} onChange={e => { const l = [...linkings]; l[i] = { ...l[i], sortOrder: e.target.value }; setLinkings(l); }} placeholder="10" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="customSortOrder">
                      <Input value={lnk.customSortOrder} onChange={e => { const l = [...linkings]; l[i] = { ...l[i], customSortOrder: e.target.value }; setLinkings(l); }} placeholder="A-001" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="url">
                      <Input value={lnk.url} onChange={e => { const l = [...linkings]; l[i] = { ...l[i], url: e.target.value }; setLinkings(l); }} placeholder="https://..." />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setLinkings([...linkings, {}])} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
              Linking hinzufügen
            </Button>

            <Title level={5}>LinkingType</Title>
            {linkingTypes.map((lt, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`LinkingType ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setLinkingTypes(linkingTypes.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={lt.name} onChange={e => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], name: e.target.value }; setLinkingTypes(arr); }} placeholder="Typ-Bezeichnung" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="cdmmId">
                      <Input value={lt.cdmmId} onChange={e => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], cdmmId: e.target.value }; setLinkingTypes(arr); }} maxLength={128} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="url">
                      <Input value={lt.url} onChange={e => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], url: e.target.value }; setLinkingTypes(arr); }} placeholder="https://..." />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={4}><Form.Item label="forProduct"><Switch checked={lt.forProduct} onChange={v => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], forProduct: v }; setLinkingTypes(arr); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="forThing"><Switch checked={lt.forThing} onChange={v => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], forThing: v }; setLinkingTypes(arr); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="forMediaObject"><Switch checked={lt.forMediaObject} onChange={v => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], forMediaObject: v }; setLinkingTypes(arr); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="forBrand"><Switch checked={lt.forBrand} onChange={v => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], forBrand: v }; setLinkingTypes(arr); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="forOffer"><Switch checked={lt.forOffer} onChange={v => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], forOffer: v }; setLinkingTypes(arr); }} /></Form.Item></Col>
                  <Col span={4}><Form.Item label="forEligibleRegion"><Switch checked={lt.forEligibleRegion} onChange={v => { const arr = [...linkingTypes]; arr[i] = { ...arr[i], forEligibleRegion: v }; setLinkingTypes(arr); }} /></Form.Item></Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setLinkingTypes([...linkingTypes, {}])} icon={<PlusOutlined />} block>
              LinkingType hinzufügen
            </Button>
          </div>
        ),
      },
    ]} />
  );

  // ── Step 5: Media & Finish ──
  const renderMediaStep = () => (
    <Tabs defaultActiveKey="media" items={[
      {
        key: 'media',
        label: 'MediaObject',
        children: (
          <div>
            <Alert message="MediaObjectRead – Bilder, Dokumente und andere Medien zum Produkt." type="info" showIcon style={{ marginBottom: 16 }} />
            {mediaObjects.map((mo, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`MediaObject ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeMediaObject(i)} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={mo.name} onChange={e => { const m = [...mediaObjects]; m[i] = { ...m[i], name: e.target.value }; setMediaObjects(m); }} placeholder="Produktbild" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="contentUrl">
                      <Input value={mo.contentUrl} onChange={e => { const m = [...mediaObjects]; m[i] = { ...m[i], contentUrl: e.target.value }; setMediaObjects(m); }} placeholder="https://..." />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="encodingFormat">
                      <Select value={mo.encodingFormat} onChange={v => { const m = [...mediaObjects]; m[i] = { ...m[i], encodingFormat: v }; setMediaObjects(m); }} placeholder="Format">
                        <Option value="image/jpeg">JPEG</Option>
                        <Option value="image/png">PNG</Option>
                        <Option value="application/pdf">PDF</Option>
                        <Option value="video/mp4">MP4</Option>
                        <Option value="model/step">STEP</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="description">
                  <Input value={mo.description} onChange={e => { const m = [...mediaObjects]; m[i] = { ...m[i], description: e.target.value }; setMediaObjects(m); }} placeholder="Beschreibung des Medienobjekts" />
                </Form.Item>
              </Card>
            ))}
            <Button type="dashed" onClick={addMediaObject} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
              MediaObject hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'offer-currency',
        label: 'Offer / Currency',
        children: (
          <div>
            <Alert message="OfferRead & CurrencyRead – Angebote mit Preis und Währung, EligibleRegion." type="info" showIcon style={{ marginBottom: 16 }} />
            <Title level={5}>Offer</Title>
            {offers.map((off, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Offer ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setOffers(offers.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="price">
                      <InputNumber style={{ width: '100%' }} step={0.01} value={off.price} onChange={v => { const o = [...offers]; o[i] = { ...o[i], price: v ?? undefined }; setOffers(o); }} placeholder="99.99" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="priceCurrency">
                      <Input value={off.priceCurrency} onChange={e => { const o = [...offers]; o[i] = { ...o[i], priceCurrency: e.target.value }; setOffers(o); }} placeholder="EUR" maxLength={3} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={off.name} onChange={e => { const o = [...offers]; o[i] = { ...o[i], name: e.target.value }; setOffers(o); }} placeholder="Angebotsbezeichnung" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setOffers([...offers, {}])} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
              Offer hinzufügen
            </Button>

            <Title level={5}>Currency</Title>
            {currencies.map((cur, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Currency ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setCurrencies(currencies.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label="currencyAlphaCode3">
                      <Input value={cur.currencyAlphaCode3} onChange={e => { const c = [...currencies]; c[i] = { ...c[i], currencyAlphaCode3: e.target.value }; setCurrencies(c); }} placeholder="EUR" maxLength={3} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="currencyCodeNumeric">
                      <InputNumber style={{ width: '100%' }} value={cur.currencyCodeNumeric} onChange={v => { const c = [...currencies]; c[i] = { ...c[i], currencyCodeNumeric: v ?? undefined }; setCurrencies(c); }} placeholder="978" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="currencySymbol">
                      <Input value={cur.currencySymbol} onChange={e => { const c = [...currencies]; c[i] = { ...c[i], currencySymbol: e.target.value }; setCurrencies(c); }} placeholder="€" maxLength={512} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="letsCode">
                      <Input value={cur.letsCode} onChange={e => { const c = [...currencies]; c[i] = { ...c[i], letsCode: e.target.value }; setCurrencies(c); }} maxLength={32} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setCurrencies([...currencies, {}])} icon={<PlusOutlined />} block>
              Currency hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'thing',
        label: 'Thing',
        children: (
          <div>
            <Alert message="ThingRead – Allgemeine Objekte/Gegenstände mit Kategorie- und Organisationszuordnung." type="info" showIcon style={{ marginBottom: 16 }} />
            {things.map((t, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8 }} title={`Thing ${i + 1}`}
                extra={<Button type="text" danger icon={<DeleteOutlined />} onClick={() => setThings(things.filter((_, idx) => idx !== i))} />}>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="name">
                      <Input value={t.name} onChange={e => { const arr = [...things]; arr[i] = { ...arr[i], name: e.target.value }; setThings(arr); }} placeholder="Bezeichnung" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="cdmmId">
                      <Input value={t.cdmmId} onChange={e => { const arr = [...things]; arr[i] = { ...arr[i], cdmmId: e.target.value }; setThings(arr); }} maxLength={128} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="url">
                      <Input value={t.url} onChange={e => { const arr = [...things]; arr[i] = { ...arr[i], url: e.target.value }; setThings(arr); }} placeholder="https://..." />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="description">
                  <TextArea rows={2} value={t.description} onChange={e => { const arr = [...things]; arr[i] = { ...arr[i], description: e.target.value }; setThings(arr); }} placeholder="Beschreibung" />
                </Form.Item>
              </Card>
            ))}
            <Button type="dashed" onClick={() => setThings([...things, {}])} icon={<PlusOutlined />} block>
              Thing hinzufügen
            </Button>
          </div>
        ),
      },
      {
        key: 'summary',
        label: 'Abschluss',
        children: (
          <div>
            {!loading && !qrCodeUrl && (
              <Alert
                message="Zusammenfassung"
                description={
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li><strong>Produkt:</strong> {product.name || '–'} ({product.gtin || product.mpn || 'keine ID'})</li>
                    <li><strong>Hersteller:</strong> {manufacturer.legalName || '–'}</li>
                    <li><strong>Person:</strong> {person.givenName && person.familyName ? `${person.givenName} ${person.familyName}` : '–'}</li>
                    <li><strong>Marke:</strong> {brand.name || '–'}</li>
                    <li><strong>ProductGroup:</strong> {productGroup.name || '–'}</li>
                    <li><strong>ProductModel:</strong> {productModel.name || '–'}</li>
                    <li><strong>Kategorien:</strong> {categories.filter(c => c.category).length}</li>
                    <li><strong>Eigenschaften:</strong> {attributes.length} | Properties: {properties.length} | Units: {units.length} | Values: {values.length}</li>
                    <li><strong>Zertifizierungen:</strong> {certifications.length}</li>
                    <li><strong>Messungen:</strong> {observations.length}</li>
                    <li><strong>Linkings:</strong> {linkings.length} | LinkingTypes: {linkingTypes.length}</li>
                    <li><strong>Medien:</strong> {mediaObjects.length}</li>
                    <li><strong>Offers:</strong> {offers.length} | Currencies: {currencies.length}</li>
                    <li><strong>Languages:</strong> {languages.length} | Dictionaries: {dictionaries.length}</li>
                    <li><strong>Things:</strong> {things.length}</li>
                  </ul>
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                style={{ marginTop: 16 }}
              />
            )}

            {loading && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Progress type="circle" percent={progress} status="active" />
                <div style={{ marginTop: 16 }}>DPP wird erstellt und validiert...</div>
              </div>
            )}

            {qrCodeUrl && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                <Title level={4} style={{ color: '#52c41a' }}>DPP erfolgreich erstellt!</Title>
                <img src={qrCodeUrl} alt="DPP QR Code" style={{ maxWidth: 200, margin: '16px 0' }} />
                <div>
                  <Space>
                    <Button type="primary" icon={<SaveOutlined />}>DPP speichern</Button>
                    <Button icon={<QrcodeOutlined />}>QR-Code drucken</Button>
                  </Space>
                </div>
              </div>
            )}
          </div>
        ),
      },
    ]} />
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderProductStep();
      case 1: return renderManufacturerStep();
      case 2: return renderClassificationStep();
      case 3: return renderAttributesStep();
      case 4: return renderCertificationsStep();
      case 5: return renderMediaStep();
      default: return null;
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Card title="Digital Product Pass erstellen (DPP)" bordered={false}>
        <Steps current={currentStep} items={steps} size="small" style={{ marginBottom: 24 }} />

        <Form form={form} layout="vertical">
          {renderStepContent()}

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              {currentStep > 0 && (
                <Button onClick={prevStep}>Zurück</Button>
              )}
              {currentStep < steps.length - 1 && (
                <Button type="primary" onClick={nextStep}>Weiter</Button>
              )}
              {currentStep === steps.length - 1 && !qrCodeUrl && (
                <Button type="primary" onClick={onSubmit} loading={loading} disabled={loading}>
                  DPP erstellen & validieren
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default DppCreation;
